import { addInvoiceToStats, updateInvoiceInStats, removeInvoiceFromStats } from "../services/statsService";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Printer, FolderOpen, Trash2, Plus, RotateCcw } from "lucide-react";
import { notify } from "../utils/notify";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import { getCompanySettings } from "../services/companySettingsService";
import { generateInvoiceNumber } from "../services/invoiceNumberService";
import generateInvoicePDF from "../utils/generateInvoicePDF";
import InvoicePdfToolbar from "../components/InvoicePdfToolbar";
import InvoiceReminderToolbar from "../components/InvoiceReminderToolbar";
import UIButton from "../components/UIButton";

const EMPTY_ITEM = { description: "", quantity: 1, unitPrice: 0 };
const HISTORY_PAGE_SIZE = 8;

function getInvoicePaymentStatus({ paidAmount = 0, grandTotal = 0, dueDate }) {
  const paid = Number(paidAmount || 0);
  const total = Number(grandTotal || 0);

  if (total > 0 && paid >= total - 0.01) return "paid";
  if (paid > 0 && paid < total - 0.01) return "partial";

  if (dueDate) {
    const due = new Date(dueDate);
    const now = new Date();
    due.setHours(23, 59, 59, 999);
    if (!Number.isNaN(due.getTime()) && now > due) return "overdue";
  }

  return "unpaid";
}

function getInvoiceStatusLabel(status, labels) {
  if (status === "paid") return labels.paidFull;
  if (status === "partial") return labels.paidPartial;
  if (status === "overdue") return labels.overdue;
  return labels.unpaid;
}

function getInvoiceStatusBadgeClasses(status, theme) {
  const isDark = theme === "dark";

  const map = {
    paid: isDark
      ? "bg-emerald-900/40 text-emerald-300 border border-emerald-800/60"
      : "bg-emerald-50 text-emerald-700 border border-emerald-200",
    partial: isDark
      ? "bg-amber-900/40 text-amber-300 border border-amber-800/60"
      : "bg-amber-50 text-amber-700 border border-amber-200",
    overdue: isDark
      ? "bg-rose-900/40 text-rose-300 border border-rose-800/60"
      : "bg-rose-50 text-rose-700 border border-rose-200",
    unpaid: isDark
      ? "bg-slate-800 text-slate-200 border border-slate-700"
      : "bg-slate-100 text-slate-700 border border-slate-200",
  };

  return map[status] || map.unpaid;
}


export default function Invoice({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const printRef = useRef(null);

  const [company, setCompany] = useState({
    companyName: "",
    ownerName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    logoUrl: "",
    invoicePrefix: "BAU",
    currency: "EUR",
    iban: "",
    bankName: "",
    taxNumber: "",
    businessNumber: "",
  });

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [dueDate, setDueDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyCursorStack, setHistoryCursorStack] = useState([null]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Shtuar variabli për shumën e paguar
  const [paidAmount, setPaidAmount] = useState(0);

  // Zgjedhja e TVSH-së (0, 7, 19, ose custom)
  const [vatType, setVatType] = useState("0"); 
  const [customVatRate, setCustomVatRate] = useState(0);

  const isGerman = lang === "de";
  const isAlbanian = lang === "sq";

  const labels = useMemo(
    () => ({
      title: t.invoiceTitle || (isGerman ? "Rechnung" : isAlbanian ? "Faturë" : "Invoice"),
      subtitle:
        t.invoiceSubtitle ||
        (isGerman
          ? "Erstelle professionelle Rechnungen und speichere sie im System."
          : isAlbanian
          ? "Krijo fatura profesionale dhe ruaji në sistem."
          : "Create professional invoices and save them in the system."),
      createTitle:
        t.invoiceCreateTitle ||
        (isGerman ? "Rechnung erstellen" : isAlbanian ? "Krijo faturë" : "Create invoice"),
      createSubtitle:
        t.invoiceCreateSubtitle ||
        (isGerman
          ? "Fülle Kundendaten, Positionen und Zahlungsdetails aus."
          : isAlbanian
          ? "Plotëso të dhënat e klientit, artikujt dhe pagesën."
          : "Fill in client data, items and payment details."),
      historyTitle:
        t.invoiceHistoryTitle ||
        (isGerman ? "Rechnungsverlauf" : isAlbanian ? "Historia e faturave" : "Invoice history"),
      historySubtitle:
        t.invoiceHistorySubtitle ||
        (isGerman
          ? "Die zuletzt gespeicherten Rechnungen."
          : isAlbanian
          ? "Faturat e ruajtura së fundi."
          : "Most recently saved invoices."),
      previewTitle:
        t.invoicePreviewTitle ||
        t.invoiceDocumentPreview ||
        (isGerman ? "Vorschau der Rechnung" : isAlbanian ? "Parapamja e faturës" : "Invoice preview"),
      previewSubtitle:
        t.invoicePreviewSubtitle ||
        t.invoiceDocumentPreviewSubtitle ||
        (isGerman
          ? "Die Dokumentvorschau zeigt dir das fertige Ergebnis vor dem Drucken"
          : isAlbanian
          ? "Parapamja e dokumentit të tregon rezultatin përfundimtar para printimit"
          : "The document preview shows you the final result before printing"),
      customer: t.invoiceCustomer || (isGerman ? "Kunde" : isAlbanian ? "Klienti" : "Customer"),
      customerEmail: t.invoiceCustomerEmail || "Email",
      customerPhone:
        t.invoiceCustomerPhone || (isGerman ? "Telefon" : isAlbanian ? "Telefoni" : "Phone"),
      customerAddress:
        t.invoiceCustomerAddress || (isGerman ? "Adresse" : isAlbanian ? "Adresa" : "Address"),
      selectClient:
        t.invoiceSelectClient || (isGerman ? "Kunde auswählen" : isAlbanian ? "Zgjidh klientin" : "Select client"),
      newClient: t.invoiceNewClient || (isGerman ? "Neuer Kunde" : isAlbanian ? "Klient i ri" : "New client"),
      saveClientHint:
        t.invoiceSaveClientHint ||
        (isGerman
          ? "Der Kunde wird automatisch gespeichert, wenn du eine Rechnung erstellst."
          : isAlbanian
          ? "Klienti ruhet automatikisht kur krijon një faturë."
          : "The client is automatically saved when you create an invoice."),
      invoiceNumber:
        t.invoiceNumber || (isGerman ? "Rechnungsnummer" : isAlbanian ? "Numri i faturës" : "Invoice number"),
      invoiceDate:
        t.invoiceDate || (isGerman ? "Rechnungsdatum" : isAlbanian ? "Data e faturës" : "Invoice date"),
      dueDate:
        t.invoiceDueDate || (isGerman ? "Fälligkeitsdatum" : isAlbanian ? "Afati i pagesës" : "Due date"),
      items: t.invoiceItems || (isGerman ? "Positionen" : isAlbanian ? "Artikujt" : "Items"),
      description: t.description || (isGerman ? "Beschreibung" : isAlbanian ? "Përshkrimi" : "Description"),
      quantity: t.quantity || (isGerman ? "Menge" : isAlbanian ? "Sasia" : "Quantity"),
      unitPrice:
        t.invoiceUnitPrice || (isGerman ? "Preis / Einheit" : isAlbanian ? "Çmimi / njësi" : "Unit price"),
      lineTotal: t.invoiceLineTotal || (isGerman ? "Gesamt" : isAlbanian ? "Totali" : "Total"),
      addItem:
        t.invoiceAddItem || (isGerman ? "Position hinzufügen" : isAlbanian ? "Shto artikull" : "Add item"),
      notes: t.notes || (isGerman ? "Notizen" : isAlbanian ? "Shënime" : "Notes"),
      save: t.invoiceSave || (isGerman ? "Rechnung speichern" : isAlbanian ? "Ruaj faturën" : "Save invoice"),
      update: t.invoiceUpdate || (isGerman ? "Aktualisieren" : isAlbanian ? "Përditëso faturën" : "Update invoice"),
      delete: t.delete || (isGerman ? "Löschen" : isAlbanian ? "Fshij" : "Delete"),
      confirmDelete: t.confirmDelete || (isGerman ? "Möchten Sie diese Rechnung wirklich löschen?" : isAlbanian ? "A jeni i sigurt që dëshironi ta fshini këtë faturë?" : "Are you sure you want to delete this invoice?"),
      saving:
        t.invoiceSaving || (isGerman ? "Wird gespeichert..." : isAlbanian ? "Duke ruajtur..." : "Saving..."),
      print: t.invoicePrint || (isGerman ? "Drucken / PDF" : isAlbanian ? "Printo / PDF" : "Print / PDF"),
      refresh: t.invoiceRefresh || (isGerman ? "Aktualisieren" : isAlbanian ? "Rifresko" : "Refresh"),
      suiteBadge:
        t.invoiceSuiteBadge ||
        (isGerman ? "BauHeld Rechnungssuite" : isAlbanian ? "BauHeld Faturat" : "BauHeld Billing Suite"),
      clientsStat:
        t.invoiceClientsStat || (isGerman ? "Kunden" : isAlbanian ? "Klientë" : "Clients"),
      invoicesStat:
        t.invoiceInvoicesStat || (isGerman ? "Rechnungen" : isAlbanian ? "Fatura" : "Invoices"),
      pdfToolsTitle:
        t.invoicePdfToolsTitle || (isGerman ? "PDF & Druck" : isAlbanian ? "PDF & Printim" : "PDF & Print"),
      pdfCopyName:
        t.invoiceCopyPdfName ||
        (isGerman ? "PDF-Namen kopieren" : isAlbanian ? "Kopjo emrin e PDF-së" : "Copy PDF name"),
      printHint:
        t.invoicePrintHint ||
        (isGerman ? "Rechnung drucken oder PDF-Dateiname kopieren" : isAlbanian ? "Printo faturën ose kopjo emrin e skedarit PDF" : "Print the invoice or copy the PDF file name"),
      noInvoices:
        t.invoiceNoInvoices ||
        (isGerman ? "Noch keine Rechnungen vorhanden" : isAlbanian ? "Ende nuk ka fatura" : "No invoices yet"),
      noInvoicesSubtitle:
        t.invoiceNoInvoicesSubtitle ||
        (isGerman
          ? "Erstelle die erste Rechnung und sie wird hier angezeigt."
          : isAlbanian
          ? "Krijo faturën e parë dhe do të shfaqet këtu."
          : "Create the first invoice and it will appear here."),
      noClients:
        t.invoiceNoClients ||
        (isGerman ? "Noch keine Kunden gespeichert" : isAlbanian ? "Ende nuk ka klientë të ruajtur" : "No saved clients yet"),
      saveError:
        t.invoiceSaveError ||
        (isGerman
          ? "Fehler beim Speichern der Rechnung."
          : isAlbanian
          ? "Gabim gjatë ruajtjes/përditësimit të faturës."
          : "Error saving invoice."),
      companySection:
        t.invoiceCompanySection ||
        (isGerman ? "Firmendaten" : isAlbanian ? "Të dhënat e firmës" : "Company data"),
      paymentInfo:
        t.invoicePaymentInfo ||
        (isGerman ? "Zahlungsinformationen" : isAlbanian ? "Informacioni i pagesës" : "Payment information"),
      open: t.invoiceOpen || (isGerman ? "Öffnen" : isAlbanian ? "Hape" : "Open"),
      newInvoice: t.invoiceNew || (isGerman ? "Neu" : isAlbanian ? "E re" : "New"),
      thankYou:
        t.invoiceThankYou ||
        (isGerman ? "Vielen Dank für Ihr Vertrauen." : isAlbanian ? "Faleminderit për besimin tuaj." : "Thank you for your trust."),
      companyData:
        t.invoiceCompanyData || (isGerman ? "Firmendaten" : isAlbanian ? "Të dhënat e firmës" : "Company data"),
      recipientSection:
        t.invoiceRecipientSection || (isGerman ? "Empfängername" : isAlbanian ? "Marrësi" : "Recipient"),
      billTo: t.invoiceBillTo || (isGerman ? "Rechnung an" : isAlbanian ? "Fatura për" : "Bill to"),
      subtotalLabel:
        t.invoiceSubtotal || (isGerman ? "Zwischensumme" : isAlbanian ? "Nëntotali" : "Subtotal"),
      vatLabel:
        t.invoiceVat || (isGerman ? "MwSt." : isAlbanian ? "TVSH" : "VAT"),
      customVatLabel:
        t.invoiceCustomVat || (isGerman ? "Andere" : isAlbanian ? "Tjetër" : "Custom"),
      grandTotalLabel:
        t.invoiceGrandTotal || (isGerman ? "Gesamtsumme" : isAlbanian ? "Totali i Përgjithshëm" : "Grand Total"),
      downloadPdf:
        t.invoiceDownloadPdf || (isGerman ? "PDF herunterladen" : isAlbanian ? "Shkarko PDF" : "Download PDF"),
      emptyPreview:
        t.invoiceEmptyPreview ||
        (isGerman
          ? "Die Live-Vorschau wird automatisch aktualisiert."
          : isAlbanian
          ? "Parapamja live përditësohet automatikisht."
          : "The live preview updates automatically."),
      clientPresetsTitle:
        t.invoiceClientPresetsTitle ||
        (isGerman ? "Kundenliste" : isAlbanian ? "Lista e klientëve" : "Client list"),
      clientPresetsSubtitle:
        t.invoiceClientPresetsSubtitle ||
        (isGerman
          ? "Wähle einen gespeicherten Kunden oder erstelle einen neuen."
          : isAlbanian
          ? "Zgjidh një klient të ruajtur ose krijo një të ri."
          : "Choose a saved client or create a new one."),
      loading: t.loading || (isGerman ? "Wird geladen..." : isAlbanian ? "Duke ngarkuar..." : "Loading..."),
      paymentStatusTitle:
        t.invoicePaymentStatusTitle ||
        (isGerman
          ? "Zahlungsstatus (Bezahlter Betrag)"
          : isAlbanian
          ? "Statusi i Pagesës (Shuma e paguar)"
          : "Payment status (Paid amount)"),
      invoiceTotalAmount:
        t.invoiceTotalAmount ||
        (isGerman ? "Rechnungsbetrag" : isAlbanian ? "Totali faturës" : "Invoice total"),
      unpaid:
        t.invoiceUnpaid ||
        (isGerman ? "Unbezahlt" : isAlbanian ? "E papaguar" : "Unpaid"),
      paidFull:
        t.invoicePaidFull ||
        (isGerman ? "Vollständig bezahlt" : isAlbanian ? "E paguar plotësisht" : "Fully paid"),
      paidPartial:
        t.invoicePaidPartial ||
        (isGerman ? "Teilweise bezahlt" : isAlbanian ? "Pjesërisht" : "Partially paid"),
      overdue:
        t.invoiceOverdue ||
        (isGerman ? "Überfällig" : isAlbanian ? "Me vonesë" : "Overdue"),
      statusAuto:
        t.invoiceStatusAuto ||
        (isGerman ? "Automatischer Status" : isAlbanian ? "Status automatik" : "Automatic status"),
      page:
        t.invoicePage ||
        (isGerman ? "Seite" : isAlbanian ? "Faqja" : "Page"),
      previous:
        t.invoicePrevious ||
        (isGerman ? "Zurück" : isAlbanian ? "Prapa" : "Previous"),
      next:
        t.invoiceNext ||
        (isGerman ? "Weiter" : isAlbanian ? "Tjetra" : "Next"),
    }),
    [t, isGerman, isAlbanian]
  );

  const currentVatRate = useMemo(() => {
    return vatType === "custom" ? Number(customVatRate) : Number(vatType);
  }, [vatType, customVatRate]);

  const draftSubtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum +
          (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
        0
      ),
    [items]
  );

  const draftVatAmount = Number((draftSubtotal * (currentVatRate / 100)).toFixed(2));
  const draftGrandTotal = draftSubtotal + draftVatAmount;

  const selectedClient = useMemo(() => {
    const fromList = clients.find((c) => c.id === selectedClientId);
    if (fromList) return fromList;

    return {
      id: selectedClientId || "",
      name: clientName || "",
      email: clientEmail || "",
      phone: clientPhone || "",
      address: clientAddress || "",
      companyName: "",
    };
  }, [
    clients,
    selectedClientId,
    clientName,
    clientEmail,
    clientPhone,
    clientAddress,
  ]);

  const activeInvoice = useMemo(() => {
    const liveClient = {
      name: clientName,
      email: clientEmail,
      phone: clientPhone,
      address: clientAddress,
    };

    if (selectedInvoice) {
      return {
        ...selectedInvoice,
        company: {
          ...(selectedInvoice.company || {}),
          ...company,
        },
        client: {
          ...(selectedInvoice.client || {}),
          ...liveClient,
        },
        currency:
          selectedInvoice.currency || company.currency || "EUR",
      };
    }

    return {
      company,
      client: liveClient,
      invoiceNumber,
      invoiceDate,
      dueDate,
      notes,
      items,
      subtotal: draftSubtotal,
      vatRate: currentVatRate,
      vatAmount: draftVatAmount,
      total: draftGrandTotal,
      currency: company.currency || "EUR",
    };
  }, [
      selectedInvoice,
      company,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      invoiceNumber,
      invoiceDate,
      dueDate,
      notes,
      items,
      draftSubtotal,
      currentVatRate,
      draftVatAmount,
      draftGrandTotal,
    ]
  );

  const reminderInvoice = useMemo(
    () => ({
      invoiceNumber: activeInvoice?.invoiceNumber || invoiceNumber,
      number: activeInvoice?.invoiceNumber || invoiceNumber,
      dueDate: activeInvoice?.dueDate || dueDate,
      grandTotal: activeInvoice?.total || draftGrandTotal,
      total: activeInvoice?.total || draftGrandTotal,
      paidAmount: paidAmount || 0,
      clientName: selectedClient?.name || "",
      companyName: selectedClient?.companyName || "",
    }),
    [activeInvoice, invoiceNumber, dueDate, draftGrandTotal, selectedClient, paidAmount]
  );

  const formatCurrency = (
    value,
    currencyCode = activeInvoice?.currency || company.currency || "EUR"
  ) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currencyCode,
    }).format(Number(value) || 0);

  const generateFreshInvoiceNumber = async (dateValue = invoiceDate) => {
    try {
      const settings = await getCompanySettings(false);
      const nextNumber = await generateInvoiceNumber({
        prefix: settings.invoicePrefix || company.invoicePrefix || "BAU",
        mode: settings.invoiceNumberMode || "yearly",
        date: dateValue || new Date(),
      });
      setInvoiceNumber(nextNumber);
      return nextNumber;
    } catch (error) {
      console.error("Error generating invoice number:", error);
      const fallbackPrefix = company.invoicePrefix || "BAU";
      const fallback = `${fallbackPrefix}-${String(history.length + 1).padStart(
        4,
        "0"
      )}`;
      setInvoiceNumber(fallback);
      return fallback;
    }
  };

  const loadHistoryPage = async ({ cursor = null, page = 1, appendCursor = false } = {}) => {
    try {
      setHistoryLoading(true);

      const invoiceQuery = cursor
        ? query(
            collection(db, "invoices"),
            orderBy("createdAt", "desc"),
            startAfter(cursor),
            limit(HISTORY_PAGE_SIZE + 1)
          )
        : query(
            collection(db, "invoices"),
            orderBy("createdAt", "desc"),
            limit(HISTORY_PAGE_SIZE + 1)
          );

      const invoicesSnap = await getDocs(invoiceQuery);
      const docs = invoicesSnap.docs;
      const hasMore = docs.length > HISTORY_PAGE_SIZE;
      const visibleDocs = hasMore ? docs.slice(0, HISTORY_PAGE_SIZE) : docs;
      const nextCursor = visibleDocs.length ? visibleDocs[visibleDocs.length - 1] : null;

      const invoiceDocs = visibleDocs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setHistory(invoiceDocs);
      setHistoryHasMore(hasMore);
      setHistoryPage(page);

      if (appendCursor && nextCursor) {
        setHistoryCursorStack((prev) => {
          const next = [...prev];
          next[page] = nextCursor;
          return next;
        });
      }

      if (!appendCursor) {
        setHistoryCursorStack((prev) => {
          const next = [...prev];
          next[page - 1] = cursor;
          if (nextCursor) next[page] = nextCursor;
          return next;
        });
      }
    } catch (error) {
      console.error("Error loading paginated invoice history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const [settingsData, clientsSnap] = await Promise.all([
        getCompanySettings(false),
        getDocs(
          query(
            collection(db, "invoice_clients"),
            orderBy("createdAt", "desc"),
            limit(50)
          )
        ),
      ]);

      if (settingsData) {
        setCompany((prev) => ({ ...prev, ...settingsData }));
      }

      const clientDocs = clientsSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setClients(clientDocs);

      setInvoiceNumber((prev) => {
        if (prev) return prev;
        const prefix = settingsData?.invoicePrefix || company.invoicePrefix || "BAU";
        return `${prefix}-....`;
      });

      await loadHistoryPage({ cursor: null, page: 1, appendCursor: false });
    } catch (error) {
      console.error("Error loading invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadData();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */
  const handleNextHistoryPage = async () => {
    if (!historyHasMore) return;
    const cursor = historyCursorStack[historyPage];
    if (!cursor) return;
    await loadHistoryPage({
      cursor,
      page: historyPage + 1,
      appendCursor: true,
    });
  };

  const handlePrevHistoryPage = async () => {
    if (historyPage <= 1) return;
    const previousPage = historyPage - 1;
    const previousCursor = historyCursorStack[previousPage - 1] || null;
    await loadHistoryPage({
      cursor: previousCursor,
      page: previousPage,
      appendCursor: false,
    });
  };


  const updateItem = (index, key, value) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      )
    );
  };

  const addItemRow = () => {
    setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  };

  const removeItemRow = (index) => {
    setItems((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );
  };

  const resetForm = () => {
    const today = new Date().toISOString().slice(0, 10);
    setSelectedClientId("");
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setClientAddress("");
    setInvoiceDate(today);
    setDueDate(today);
    setNotes("");
    setItems([{ ...EMPTY_ITEM }]);
    setSelectedInvoice(null);
    setVatType("0");
    setCustomVatRate(0);
    setPaidAmount(0); // Pastron formën e pagesës

    const prefix = company.invoicePrefix || "BAU";
    setInvoiceNumber(`${prefix}-....`);
  };

  const openInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setSelectedClientId(invoice.clientId || "");
    setClientName(invoice.client?.name || "");
    setClientEmail(invoice.client?.email || "");
    setClientPhone(invoice.client?.phone || "");
    setClientAddress(invoice.client?.address || "");
    setInvoiceNumber(invoice.invoiceNumber || "");
    setInvoiceDate(invoice.invoiceDate || new Date().toISOString().slice(0, 10));
    setDueDate(invoice.dueDate || new Date().toISOString().slice(0, 10));
    setNotes(invoice.notes || "");
    setPaidAmount(Number(invoice.paidAmount) || 0); // Ngarkon shumën e paguar
    
    // Ngarko TVSH-në
    const fetchedVat = invoice.vatRate !== undefined ? Number(invoice.vatRate) : 0;
    if ([0, 7, 19].includes(fetchedVat)) {
      setVatType(fetchedVat.toString());
    } else {
      setVatType("custom");
      setCustomVatRate(fetchedVat);
    }

    setItems(
      Array.isArray(invoice.items) && invoice.items.length
        ? invoice.items.map((item) => ({
            description: item.description || "",
            quantity: Number(item.quantity) || 0,
            unitPrice: Number(item.unitPrice) || 0,
          }))
        : [{ ...EMPTY_ITEM }]
    );
  };

  const deleteInvoice = async (e, id) => {
    e.stopPropagation();
    if (window.confirm(labels.confirmDelete)) {
      try {
        const invoiceToDelete = history.find((row) => row.id === id);
        await deleteDoc(doc(db, "invoices", id));
        if (invoiceToDelete) {
          await removeInvoiceFromStats(invoiceToDelete);
        }
        if (selectedInvoice?.id === id) {
          resetForm();
        }
        notify.success(
          t.deletedSuccessfully ||
            (isGerman ? "Rechnung erfolgreich gelöscht." : isAlbanian ? "Fatura u fshi me sukses." : "Invoice deleted successfully.")
        );
        await loadData();
      } catch (error) {
        console.error("Error deleting invoice:", error);
        notify.error(
          t.deleteError ||
            (isGerman ? "Fehler beim Löschen der Rechnung." : isAlbanian ? "Gabim gjatë fshirjes së faturës." : "Error deleting invoice.")
        );
      }
    }
  };

  const applyClient = (client) => {
    setSelectedClientId(client.id || "");
    setClientName(client.name || "");
    setClientEmail(client.email || "");
    setClientPhone(client.phone || "");
    setClientAddress(client.address || "");
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);

    if (!clientId) {
      setClientName("");
      setClientEmail("");
      setClientPhone("");
      setClientAddress("");
      return;
    }

    const found = clients.find((c) => c.id === clientId);
    if (found) applyClient(found);
  };

  const saveClientPreset = async () => {
    if (!clientName.trim()) return "";

    const normalizedName = clientName.trim().toLowerCase();
    const existing = clients.find(
      (c) =>
        (c.name || "").trim().toLowerCase() === normalizedName &&
        (c.email || "").trim().toLowerCase() ===
          (clientEmail || "").trim().toLowerCase()
    );

    if (existing) return existing.id;

    const ref = await addDoc(collection(db, "invoice_clients"), {
      name: clientName || "",
      email: clientEmail || "",
      phone: clientPhone || "",
      address: clientAddress || "",
      createdAt: serverTimestamp(),
    });
    return ref.id;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;

    try {
      setSaving(true);

      const clientId = await saveClientPreset();

      let finalInvoiceNumber = invoiceNumber;
      if (!finalInvoiceNumber || finalInvoiceNumber.includes("....")) {
        finalInvoiceNumber = await generateFreshInvoiceNumber(invoiceDate);
      }

      const payload = {
        company,
        clientId: clientId || selectedClientId || "",
        client: {
          name: clientName,
          email: clientEmail,
          phone: clientPhone,
          address: clientAddress,
        },
        invoiceNumber: finalInvoiceNumber,
        invoiceDate,
        dueDate,
        notes,
        items: items.map((item) => ({
          description: item.description || "",
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
        })),
        subtotal: draftSubtotal,
        vatRate: currentVatRate,
        vatAmount: draftVatAmount,
        total: draftGrandTotal,
        grandTotal: draftGrandTotal,
        paidAmount: Number(paidAmount || 0), // Ruhet shuma e paguar e caktuar nga perdoruesi
        currency: company.currency || "EUR",
        clientName: clientName || "",
        companyName: selectedClient?.companyName || "",
      };

      if (selectedInvoice?.id) {
        const previousInvoice = selectedInvoice;

        await updateDoc(doc(db, "invoices", selectedInvoice.id), payload);

        const updatedInvoice = {
          id: selectedInvoice.id,
          ...previousInvoice,
          ...payload,
          createdAt: previousInvoice.createdAt || new Date().toISOString(),
        };

        setSelectedInvoice(updatedInvoice);
        await updateInvoiceInStats(previousInvoice, updatedInvoice);

        notify.success(
          t.updatedSuccessfully ||
            (isGerman ? "Rechnung erfolgreich aktualisiert." : isAlbanian ? "Fatura u përditësua me sukses." : "Invoice updated successfully.")
        );
      } else {
        const payloadWithCreatedAt = {
          ...payload,
          createdAt: serverTimestamp(),
        };

        const docRef = await addDoc(collection(db, "invoices"), payloadWithCreatedAt);

        const savedInvoice = {
          id: docRef.id,
          ...payload,
          createdAt: new Date().toISOString(),
        };

        setSelectedInvoice(savedInvoice);
        await addInvoiceToStats(savedInvoice);

        notify.success(
          t.savedSuccessfully ||
            (isGerman ? "Rechnung erfolgreich gespeichert." : isAlbanian ? "Fatura u ruajt me sukses." : "Invoice saved successfully.")
        );
      }

      await loadData();
    } catch (error) {
      console.error("Error saving/updating invoice:", error);
      notify.error(labels.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;

    const printWindow = window.open("", "_blank", "width=980,height=1300");
    if (!printWindow) return;

    const html = content.innerHTML;

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>${activeInvoice?.invoiceNumber || invoiceNumber || "Invoice"}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 6mm;
            }

            html, body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #0f172a;
              font-family: Arial, Helvetica, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            body {
              overflow: hidden;
            }

            .invoice-document-shell {
              display: flex;
              justify-content: center;
              align-items: flex-start;
              padding: 0;
            }

            .invoice-document {
              width: 198mm;
              min-height: auto;
              background: #ffffff;
              color: #0f172a;
              border-radius: 0;
              border: none;
              padding: 8mm;
              box-shadow: none;
              box-sizing: border-box;
              margin: 0 auto;
              background-image:
                radial-gradient(circle at top right, rgba(59,130,246,0.08), transparent 28%),
                radial-gradient(circle at top left, rgba(16,185,129,0.05), transparent 22%);
            }

            .invoice-document .doc-grid-2 {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 16px;
            }

            .invoice-card {
              border: 1px solid #dbe5f2;
              border-radius: 22px;
              padding: 14px;
              background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
              box-shadow: none;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .invoice-table {
              width: 100%;
              border-collapse: separate;
              border-spacing: 0;
              overflow: hidden;
              border: 1px solid #dbe5f2;
              border-radius: 22px;
            }

            .invoice-table thead th {
              background: #061126;
              color: #fff;
              font-size: 12px;
              font-weight: 700;
              padding: 10px 12px;
            }

            .invoice-table tbody td {
              padding: 12px;
              font-size: 13px;
              border-top: 1px solid #e5edf7;
            }

            .doc-muted {
              color: #6b7b93;
            }

            .doc-label {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.24em;
              color: #94a3b8;
              font-weight: 700;
            }

            .doc-total-card {
              border: 1px solid #dbe5f2;
              border-radius: 22px;
              padding: 14px;
              background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
              box-shadow: none;
              margin-left: auto;
            }

            .invoice-header-layout {
              display: grid;
              grid-template-columns: minmax(0, 1fr) 300px;
              gap: 24px;
              align-items: start;
              margin-bottom: 22px;
            }

            .invoice-header-left {
              min-width: 0;
            }

            .invoice-document-title {
              margin: 0 0 42px;
              font-size: 38px;
              line-height: 1;
              font-weight: 900;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              color: #1f4ea3;
            }

            .invoice-sender-block {
              margin-bottom: 18px;
            }

            .invoice-section-label {
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.14em;
              color: #94a3b8;
              font-weight: 700;
              margin-bottom: 8px;
            }

            .invoice-sender-line {
              font-size: 12px;
              line-height: 1.6;
              color: #334155;
            }

            .invoice-sender-line-primary {
              font-size: 14px;
              line-height: 1.4;
              color: #0f172a;
              font-weight: 800;
              margin-bottom: 2px;
            }

            .invoice-recipient-block {
              max-width: 420px;
            }

            .invoice-recipient-name {
              font-size: 18px;
              line-height: 1.3;
              font-weight: 900;
              color: #0f172a;
              margin-bottom: 8px;
            }

            .invoice-recipient-line {
              font-size: 14px;
              line-height: 1.6;
              color: #334155;
            }

            .invoice-header-right {
              min-width: 0;
              display: flex;
              flex-direction: column;
              align-items: stretch;
              gap: 18px;
            }

            .invoice-brand-card {
              display: flex;
              justify-content: flex-end;
              background: transparent;
              border: none;
              padding: 0;
            }

            .invoice-brand-row {
              display: grid;
              grid-template-columns: 78px minmax(0, 1fr);
              gap: 14px;
              align-items: center;
              max-width: 280px;
            }

            .invoice-logo-box {
              width: 78px;
              height: 78px;
              border: 1px solid #dbe5f2;
              border-radius: 18px;
              background: #ffffff;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
            }

            .invoice-logo-image {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }

            .invoice-logo-fallback {
              font-size: 28px;
              font-weight: 900;
              color: #0f172a;
            }

            .invoice-brand-copy {
              min-width: 0;
            }

            .invoice-brand-eyebrow {
              font-size: 10px;
              line-height: 1.4;
              text-transform: uppercase;
              letter-spacing: 0.22em;
              color: #94a3b8;
              font-weight: 700;
              margin-bottom: 6px;
            }

            .invoice-brand-name {
              font-size: 18px;
              line-height: 1.15;
              font-weight: 900;
              color: #0f172a;
              margin-bottom: 8px;
            }

            .invoice-brand-line {
              font-size: 12px;
              line-height: 1.45;
              color: #334155;
              word-break: break-word;
            }

            .invoice-meta-panel {
              border: 1px solid #dbe5f2;
              border-radius: 18px;
              background: #f8fafc;
              padding: 18px 20px;
              margin-left: auto;
              max-width: 280px;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .invoice-meta-row {
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 14px;
              align-items: center;
            }

            .invoice-meta-row + .invoice-meta-row {
              margin-top: 14px;
            }

            .invoice-meta-label {
              font-size: 14px;
              font-weight: 700;
              color: #0f172a;
            }

            .invoice-meta-value {
              font-size: 14px;
              font-weight: 700;
              color: #475569;
              text-align: right;
              white-space: nowrap;
            }
          </style>
        </head>
        <body>
          <div class="invoice-document-shell">
            ${html}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  };

  const handleDownloadPdf = async () => {
    await generateInvoicePDF(
      {
        invoiceNumber: activeInvoice?.invoiceNumber || invoiceNumber,
        date: activeInvoice?.invoiceDate || invoiceDate,
        dueDate: activeInvoice?.dueDate || dueDate,
        notes: activeInvoice?.notes || notes,
        items: (activeInvoice?.items || items).map((item) => ({
          description: item.description || "",
          qty: Number(item.quantity || 0),
          price: Number(item.unitPrice || 0),
          total:
            Number(item.quantity || 0) * Number(item.unitPrice || 0),
        })),
        subtotal: activeInvoice?.subtotal || draftSubtotal,
        vatRate: activeInvoice?.vatRate || currentVatRate,
        vatAmount: activeInvoice?.vatAmount || draftVatAmount,
        total: activeInvoice?.total || draftGrandTotal,
        currency: activeInvoice?.currency || company.currency || "EUR",
        clientName: selectedClient?.name || clientName,
        companyName: selectedClient?.companyName || "",
        client: {
          name: selectedClient?.name || clientName,
          email: selectedClient?.email || clientEmail,
          phone: selectedClient?.phone || clientPhone,
          address: selectedClient?.address || clientAddress,
        },
      },
      company
    );
  };

  return (
    <div className="space-y-6 invoice-page">
      <style>{`
        .invoice-document-shell {
          display: flex;
          justify-content: center;
        }

        .invoice-document {
          width: 210mm;
          min-height: 297mm;
          background: #f5f6f8;
          color: #0f172a;
          border-radius: 18px;
          border: 1px solid #e3e6eb;
          padding: 18mm;
          box-shadow: 0 20px 48px rgba(15, 23, 42, 0.10);
          box-sizing: border-box;
        }

        .invoice-document .doc-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .invoice-card {
          border: 1px solid #e3e6eb;
          border-radius: 16px;
          padding: 14px;
          background: #ffffff;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
          break-inside: avoid;
          page-break-inside: avoid;
        }

        .invoice-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          overflow: hidden;
          border: 1px solid #e3e6eb;
          border-radius: 16px;
          background: #ffffff;
        }

        .invoice-table thead th {
          background: #061126;
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          padding: 10px 12px;
        }

        .invoice-table tbody td {
          padding: 12px;
          font-size: 13px;
          border-top: 1px solid #e3e6eb;
        }

        .invoice-document .doc-muted {
          color: #6b7b93;
        }

        .invoice-document .doc-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.24em;
          color: #94a3b8;
          font-weight: 700;
        }

        .invoice-document .doc-total-card {
  border: 1px solid #e3e6eb;
  border-radius: 16px;
  padding: 16px;
  background: #f1f3f6;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
  margin-left: auto;
  width: 100%;
  max-width: 320px;
}

        .invoice-document .invoice-header-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 280px;
          gap: 26px;
          align-items: start;
          margin-bottom: 22px;
        }

        .invoice-document .invoice-header-left {
          min-width: 0;
        }

        .invoice-document .invoice-document-title {
          margin: 0 0 44px;
          font-size: 40px;
          line-height: 1;
          font-weight: 900;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #1e4091;
        }

        .invoice-document .invoice-sender-block {
          margin-bottom: 18px;
        }

        .invoice-document .invoice-section-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: #94a3b8;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .invoice-document .invoice-sender-line {
          font-size: 12px;
          line-height: 1.6;
          color: #334155;
        }

        .invoice-document .invoice-sender-line-primary {
          font-size: 14px;
          line-height: 1.4;
          color: #0f172a;
          font-weight: 800;
          margin-bottom: 2px;
        }

        .invoice-document .invoice-sender-inline {
          font-size: 10px;
          line-height: 1.7;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: #94a3b8;
          font-weight: 700;
        }

        .invoice-document .invoice-sender-inline-row {
          font-size: 10px;
          line-height: 1.6;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #94a3b8;
          font-weight: 700;
          max-width: 560px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .invoice-document .invoice-recipient-block {
          max-width: 420px;
        }

        .invoice-document .invoice-recipient-name {
          font-size: 14px;
          line-height: 1.3;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
        }

        .invoice-document .invoice-recipient-line {
          font-size: 14px;
          line-height: 1.55;
          color: #334155;
        }

        .invoice-document .invoice-header-right {
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 18px;
        }

        .invoice-document .invoice-brand-card {
          display: flex;
          justify-content: flex-end;
          background: transparent;
          border: none;
          padding: 0;
        }

        .invoice-document .invoice-brand-row {
          display: grid;
          grid-template-columns: 44px minmax(0, 1fr);
          gap: 10px;
          align-items: start;
          max-width: 280px;
        }

        .invoice-document .invoice-logo-box {
          width: 44px;
          height: 44px;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .invoice-document .invoice-logo-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .invoice-document .invoice-logo-fallback {
          font-size: 24px;
          font-weight: 900;
          color: #0f172a;
        }

        .invoice-document .invoice-brand-copy {
          min-width: 0;
        }

        .invoice-document .invoice-brand-eyebrow {
          display: none;
        }

        .invoice-document .invoice-brand-name {
          font-size: 18px;
          line-height: 1;
          font-weight: 900;
          color: #151515;
          margin-bottom: 6px;
          font-family: Georgia, "Times New Roman", serif;
        }

        .invoice-document .invoice-brand-line {
          font-size: 11px;
          line-height: 1.4;
          color: #2d6fbd;
          word-break: break-word;
        }

        .invoice-document .invoice-meta-panel {
          border: 1px solid #e3e6eb;
          border-radius: 12px;
          background: #f1f3f6;
          padding: 18px 18px;
          margin-left: auto;
          max-width: 280px;
        }

        .invoice-document .invoice-meta-row {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 14px;
          align-items: center;
        }

        .invoice-document .invoice-meta-row + .invoice-meta-row {
          margin-top: 16px;
        }

        .invoice-document .invoice-meta-label {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
        }

        .invoice-document .invoice-meta-value {
          font-size: 13px;
          font-weight: 500;
          color: #475569;
          text-align: right;
          white-space: nowrap;
        }


        @media screen and (max-width: 1200px) {
          .invoice-document {
            width: 100%;
            min-height: auto;
            padding: 24px;
          }
        }

        @media screen and (max-width: 767px) {
          .invoice-document .doc-grid-2,
          .invoice-document .invoice-header-layout {
            grid-template-columns: 1fr;
          }

          .invoice-document .invoice-document-title {
            font-size: 34px;
            margin-bottom: 24px;
          }

          .invoice-document .invoice-brand-card {
            justify-content: flex-start;
          }

          .invoice-document .invoice-meta-panel {
            width: 100%;
            max-width: none;
            margin-left: 0;
          }
        }
      `}</style>

      <div
        className={`relative overflow-hidden rounded-[32px] border px-5 py-5 shadow-[0_30px_80px_rgba(15,23,42,0.18)] no-print md:px-6 md:py-6 ${
          theme === "dark"
            ? "border-slate-800/80 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white"
            : "border-white/60 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white"
        }`}
      >
        <div className="absolute inset-0">
          <div className="absolute -right-10 top-0 h-40 w-40 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute left-0 top-0 h-32 w-32 rounded-full bg-blue-500/20 blur-3xl" />
        </div>

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-slate-200">
              {labels.suiteBadge}
            </div>
            <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">
              {labels.title}
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
              {labels.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatPill label={labels.grandTotalLabel} value={formatCurrency(draftGrandTotal)} />
            <StatPill label={labels.clientsStat} value={clients.length} />
            <StatPill label={labels.invoicesStat} value={history.length} />

            <div className="grid grid-cols-2 gap-3 rounded-[24px] border border-white/10 bg-white/10 p-3 backdrop-blur">
  <UIButton
    type="button"
    onClick={loadData}
    theme={theme}
    variant="ghost"
    size="lg"
    className="w-full border border-white/15 bg-white/10 text-white hover:bg-white/15"
    aria-label={labels.refresh}
    title={labels.refresh}
  >
    <RefreshCw className="h-5 w-5 md:mr-2" />
    <span className="hidden md:inline">{labels.refresh}</span>
  </UIButton>

  <UIButton
    type="button"
    onClick={handlePrint}
    theme={theme}
    variant="secondary"
    size="lg"
    className="w-full bg-white text-slate-900 hover:bg-slate-100"
    aria-label={labels.print}
    title={labels.print}
  >
    <Printer className="h-5 w-5 md:mr-2" />
    <span className="hidden md:inline">{labels.print}</span>
  </UIButton>
</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1.05fr_0.95fr]">
        <form
          onSubmit={handleSave}
          className={`rounded-[32px] border p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl no-print ${currentTheme.surface}`}
        >
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <h3 className={titleClass(theme)}>
                {selectedInvoice?.id ? labels.update : labels.createTitle}
              </h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {labels.createSubtitle}
              </p>
            </div>

            <UIButton
              type="button"
              onClick={resetForm}
              theme={theme}
              variant="ghost"
              size="md"
            >
              <RotateCcw className="h-4 w-4" />
              {labels.newInvoice}
            </UIButton>
          </div>

          <div className={softPanelClass(theme)}>
            <div className="flex items-center gap-4">
              <div className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border ${theme === "dark" ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt="Logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className={theme === "dark" ? "text-xl font-black text-slate-100" : "text-xl font-black text-slate-800"}>
                    {(company.companyName || "B").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>

              <div>
                <p className={`text-xs uppercase tracking-[0.2em] ${theme === "dark" ? "text-slate-400" : "text-slate-400"}`}>
                  {labels.companySection}
                </p>
                <h4 className={theme === "dark" ? "mt-1 text-xl font-bold text-slate-100" : "mt-1 text-xl font-bold text-slate-900"}>
                  {company.companyName || "Bauheld"}
                </h4>
                <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                  {[company.address, company.city, company.country]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </p>
              </div>
            </div>
          </div>

          <div className={`mt-6 ${softPanelClass(theme)}`}>
            <div className="mb-4">
              <h4 className={theme === "dark" ? "text-lg font-bold text-slate-100" : "text-lg font-bold text-slate-900"}>
                {labels.clientPresetsTitle}
              </h4>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {labels.clientPresetsSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
              <div>
                <label className={fieldLabelClass(theme)}>{labels.selectClient}</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => handleClientSelect(e.target.value)}
                  className={inputClass(theme)}
                >
                  <option value="">
                    {clients.length ? labels.newClient : labels.noClients}
                  </option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name || "—"} {client.email ? `· ${client.email}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <UIButton
                  type="button"
                  onClick={() => handleClientSelect("")}
                  theme={theme}
                  variant="ghost"
                  size="md"
                >
                  <Plus className="h-4 w-4" />
                  {labels.newClient}
                </UIButton>
              </div>
            </div>

            <p className={`mt-3 text-xs ${currentTheme.muted}`}>{labels.saveClientHint}</p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 md:gap-4">
            <Field label={labels.customer} value={clientName} onChange={setClientName} />
            <Field label={labels.customerEmail} value={clientEmail} onChange={setClientEmail} type="email" />
            
            <Field label={labels.customerPhone} value={clientPhone} onChange={setClientPhone} />
            <Field label={labels.invoiceNumber} value={invoiceNumber} onChange={setInvoiceNumber} />
            
            <div className="col-span-2">
              <Field label={labels.customerAddress} value={clientAddress} onChange={setClientAddress} />
            </div>
            
            <Field label={labels.invoiceDate} value={invoiceDate} onChange={setInvoiceDate} type="date" />
            <Field label={labels.dueDate} value={dueDate} onChange={setDueDate} type="date" />
          </div>

          <div className="mt-8">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className={theme === "dark" ? "text-lg font-bold text-slate-100" : "text-lg font-bold text-slate-900"}>
                {labels.items}
              </h4>
              <UIButton
                type="button"
                onClick={addItemRow}
                theme={theme}
                variant="primary"
                size="md"
              >
                <Plus className="h-4 w-4" />
                {labels.addItem}
              </UIButton>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => {
                const lineTotal =
                  (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);

                return (
                  <div
                    key={index}
                    className={`rounded-[28px] border p-4 shadow-sm ${
                      theme === "dark"
                        ? "border-slate-700 bg-gradient-to-br from-slate-900 to-slate-950"
                        : "border-slate-200/80 bg-gradient-to-br from-slate-50 to-white"
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-[1.4fr_0.6fr_0.8fr_auto]">
                      <div className="col-span-2 md:col-span-1">
                        <Field
                          label={labels.description}
                          value={item.description}
                          onChange={(v) => updateItem(index, "description", v)}
                        />
                      </div>
                      
                      <Field
                        label={labels.quantity}
                        value={item.quantity}
                        onChange={(v) => updateItem(index, "quantity", v)}
                        type="number"
                      />
                      <Field
                        label={labels.unitPrice}
                        value={item.unitPrice}
                        onChange={(v) => updateItem(index, "unitPrice", v)}
                        type="number"
                      />
                      
                      <div className="col-span-2 md:col-span-1 flex flex-col justify-end">
                        <div className={`mb-2 text-sm font-semibold ${currentTheme.muted}`}>
                          {labels.lineTotal}
                        </div>
                        <div className={`flex h-12 items-center rounded-2xl border px-4 font-bold ${
                          theme === "dark"
                            ? "border-slate-700 bg-slate-900 text-slate-100"
                            : "border-slate-200 bg-white text-slate-900"
                        }`}>
                          {formatCurrency(lineTotal)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-end">
                      <UIButton
                        type="button"
                        onClick={() => removeItemRow(index)}
                        theme={theme}
                        variant="softDanger"
                        size="sm"
                      >
                        <Trash2 className="h-4 w-4" />
                        {labels.delete}
                      </UIButton>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex flex-col items-end gap-2">
            <div className="flex w-full flex-col justify-end gap-3 md:w-auto md:flex-row md:items-center">
              <span className={`text-sm font-semibold ${currentTheme.muted}`}>{labels.vatLabel}:</span>
              
              <div className={`flex rounded-xl border p-1 shadow-sm ${
                theme === "dark" ? "border-slate-700 bg-slate-800/50" : "border-slate-200/80 bg-slate-100/50"
              }`}>
                {["0", "7", "19", "custom"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setVatType(type)}
                    className={`rounded-lg px-4 py-1.5 text-xs font-bold transition ${
                      vatType === type
                        ? theme === "dark"
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-white text-slate-900 shadow-sm"
                        : theme === "dark"
                        ? "text-slate-400 hover:text-slate-200"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {type === "custom" ? labels.customVatLabel : `${type}%`}
                  </button>
                ))}
              </div>

              {vatType === "custom" && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={customVatRate}
                    onChange={(e) => setCustomVatRate(e.target.value)}
                    className={`h-9 w-20 rounded-xl border px-3 text-right text-sm font-semibold shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-900 text-white placeholder-slate-500"
                        : "border-slate-200 bg-white text-slate-900"
                    }`}
                    placeholder="%"
                  />
                  <span className={`text-sm font-bold ${currentTheme.muted}`}>%</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8">
            <TextAreaField label={labels.notes} value={notes} onChange={setNotes} />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className={softPanelClass(theme)}>
              <p className={`text-sm ${currentTheme.muted}`}>{labels.paymentInfo}</p>
              <div className={theme === "dark" ? "mt-3 space-y-2 text-sm text-slate-300" : "mt-3 space-y-2 text-sm text-slate-700"}>
                <p>{company.bankName || "—"}</p>
                <p>{company.iban || "IBAN"}</p>
                <p>{company.email || "—"}</p>
                <p>{company.phone || "—"}</p>
              </div>
            </div>

            <div className="rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
              <div className="flex justify-between items-center text-sm text-slate-300">
                <span>{labels.subtotalLabel}</span>
                <span className="font-semibold">{formatCurrency(draftSubtotal)}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-slate-300 mt-2">
                <span>{labels.vatLabel} ({currentVatRate}%)</span>
                <span className="font-semibold">{formatCurrency(draftVatAmount)}</span>
              </div>
              <div className="mt-4 border-t border-white/10 pt-4">
                <p className="text-sm text-slate-300">{labels.grandTotalLabel}</p>
                <h4 className="mt-1 text-3xl font-bold">{formatCurrency(draftGrandTotal)}</h4>
                <p className="mt-2 text-xs text-slate-400">
                  {invoiceNumber} · {invoiceDate}
                </p>
              </div>
            </div>
          </div>

          {/* BLLOKU I STATUSIT TË PAGESËS */}
          <div className={`mt-6 rounded-[28px] border p-5 shadow-sm ${theme === "dark" ? "border-slate-700 bg-slate-900/60" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <label className={fieldLabelClass(theme)}>{labels.paymentStatusTitle}</label>
                <div className="text-sm text-slate-500">
                  {labels.invoiceTotalAmount}: <span className={theme === "dark" ? "font-bold text-slate-300" : "font-bold text-slate-700"}>{formatCurrency(draftGrandTotal)}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <UIButton
                  type="button"
                  onClick={() => setPaidAmount(0)}
                  theme={theme}
                  variant={Number(paidAmount) === 0 ? "danger" : "secondary"}
                  size="sm"
                >
                  {labels.unpaid} (0)
                </UIButton>
                <UIButton
                  type="button"
                  onClick={() => setPaidAmount(draftGrandTotal)}
                  theme={theme}
                  variant={Math.abs(Number(paidAmount) - draftGrandTotal) < 0.01 ? "success" : "secondary"}
                  size="sm"
                >
                  {labels.paidFull}
                </UIButton>
                <div className="relative flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    className={`${inputClass(theme)} w-32`}
                    placeholder="0.00"
                  />
                  <span className={`text-sm font-bold ${currentTheme.muted}`}>{company.currency || "EUR"}</span>
                </div>
              </div>
            </div>
          </div>
          {/* FUNDI I BLLOKUT TË PAGESËS */}

          <div className="mt-8 flex justify-end">
            <UIButton
              type="submit"
              disabled={saving}
              theme={theme}
              variant="primary"
              size="xl"
            >
              
              {saving ? labels.saving : (selectedInvoice?.id ? labels.update : labels.save)}
            </UIButton>
          </div>
        </form>

        <div className="space-y-6">
          <div className={`rounded-[32px] border p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl no-print ${currentTheme.surface}`}>
            <div className="mb-6">
              <h3 className={titleClass(theme)}>{labels.historyTitle}</h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {labels.historySubtitle}
              </p>
            </div>

            {loading ? (
  <div
    className={`rounded-3xl border border-dashed p-8 text-center ${
      theme === "dark"
        ? "border-slate-700 bg-slate-900/60 text-slate-400"
        : "border-slate-200 bg-slate-50 text-slate-500"
    }`}
  >
    {labels.loading}
  </div>
) : history.length === 0 ? (
  <div
    className={`rounded-3xl border border-dashed p-8 text-center ${
      theme === "dark"
        ? "border-slate-700 bg-slate-900/60"
        : "border-slate-200 bg-slate-50"
    }`}
  >
    <p
      className={
        theme === "dark"
          ? "text-base font-semibold text-slate-200"
          : "text-base font-semibold text-slate-700"
      }
    >
      {labels.noInvoices}
    </p>
    <p className={`mt-2 text-sm ${currentTheme.muted}`}>
      {labels.noInvoicesSubtitle}
    </p>
  </div>
) : (
  <div
    className={`overflow-hidden rounded-[28px] border ${
      theme === "dark"
        ? "border-slate-700 bg-slate-950/60"
        : "border-slate-200 bg-white"
    }`}
  >
   <div className="hidden 2xl:grid grid-cols-[1.3fr_1.15fr_0.95fr_0.95fr_0.95fr_1.15fr_1.05fr] gap-6 border-b px-6 py-4 text-[10px] font-extrabold uppercase tracking-[0.1em] text-slate-500 dark:border-slate-800">
      <div>{labels.invoiceNumber}</div>
      <div>{labels.customer}</div>
      <div>{labels.invoiceDate}</div>
      <div>{labels.dueDate}</div>
      <div>{labels.grandTotalLabel}</div>
      <div>{labels.statusAuto}</div>
      <div className="text-center">Aktion</div>
    </div>

    <div className="divide-y divide-slate-200 dark:divide-slate-800">
      {history.map((invoice) => {
        const invoiceStatus = getInvoicePaymentStatus({
          paidAmount: invoice.paidAmount,
          grandTotal: invoice.grandTotal || invoice.total || invoice.subtotal || 0,
          dueDate: invoice.dueDate,
        });

        return (
          <div
            key={invoice.id}
            className={`transition ${
              selectedInvoice?.id === invoice.id
                ? theme === "dark"
                  ? "bg-slate-900/80"
                  : "bg-slate-50"
                : ""
            }`}
          >
            {/* Tablet row */}
            <div className="hidden md:grid 2xl:hidden grid-cols-1 gap-3 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={theme === "dark" ? "font-bold text-slate-100" : "font-bold text-slate-900"}>
                    {invoice.invoiceNumber || "—"}
                  </div>
                  <div className={`mt-1 text-sm ${currentTheme.muted}`}>
                    {invoice.client?.name || invoice.clientName || "—"}
                  </div>
                </div>

                <span
                  className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${getInvoiceStatusBadgeClasses(
                    invoiceStatus,
                    theme
                  )}`}
                >
                  {getInvoiceStatusLabel(invoiceStatus, labels)}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
                    {labels.invoiceDate}
                  </div>
                  <div className="mt-1 break-words font-medium">
                    {invoice.invoiceDate || "—"}
                  </div>
                </div>

                <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
                    {labels.dueDate}
                  </div>
                  <div className="mt-1 break-words font-medium">
                    {invoice.dueDate || "—"}
                  </div>
                </div>

                <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
                    {labels.grandTotalLabel}
                  </div>
                  <div className="mt-1 break-words font-semibold">
                    {formatCurrency(
                      invoice.grandTotal || invoice.total || invoice.subtotal,
                      invoice.currency || company.currency || "EUR"
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <UIButton
                  type="button"
                  onClick={() => openInvoice(invoice)}
                  theme={theme}
                  variant="primary"
                  size="md"
                >
                  <FolderOpen className="h-4 w-4" />
                  {labels.open}
                </UIButton>

                <UIButton
                  type="button"
                  onClick={(e) => deleteInvoice(e, invoice.id)}
                  theme={theme}
                  variant="softDanger"
                  size="md"
                >
                  <Trash2 className="h-4 w-4" />
                  {labels.delete}
                </UIButton>
              </div>
            </div>

            {/* Desktop row */}
            <div className="hidden 2xl:grid grid-cols-[1.3fr_1.15fr_0.95fr_0.95fr_0.95fr_1.15fr_1.05fr] items-center gap-6 px-6 py-4">
              <div className="min-w-0">
                <div className={theme === "dark" ? "font-bold text-slate-100" : "font-bold text-slate-900"}>
                  {invoice.invoiceNumber || "—"}
                </div>
              </div>

              <div className="min-w-0">
                <div className={`truncate text-sm ${theme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                  {invoice.client?.name || invoice.clientName || "—"}
                </div>
              </div>

              <div className={`text-sm ${currentTheme.muted}`}>
                {invoice.invoiceDate || "—"}
              </div>

              <div className={`text-sm ${currentTheme.muted}`}>
                {invoice.dueDate || "—"}
              </div>

              <div className={theme === "dark" ? "font-semibold text-slate-100" : "font-semibold text-slate-900"}>
                {formatCurrency(
                  invoice.grandTotal || invoice.total || invoice.subtotal,
                  invoice.currency || company.currency || "EUR"
                )}
              </div>

              <div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getInvoiceStatusBadgeClasses(
                    invoiceStatus,
                    theme
                  )}`}
                >
                  {getInvoiceStatusLabel(invoiceStatus, labels)}
                </span>
              </div>

              <div className="flex justify-center gap-2">
                <UIButton
                  type="button"
                  onClick={() => openInvoice(invoice)}
                  theme={theme}
                  variant="primary"
                  size="md"
                >
                  <FolderOpen className="h-4 w-4" />
                  {labels.open}
                </UIButton>

                <UIButton
                  type="button"
                  onClick={(e) => deleteInvoice(e, invoice.id)}
                  theme={theme}
                  variant="softDanger"
                  size="md"
                >
                  <Trash2 className="h-4 w-4" />
                  {labels.delete}
                </UIButton>
              </div>
            </div>

            {/* Mobile card row */}
            <div className="md:hidden px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className={theme === "dark" ? "font-bold text-slate-100" : "font-bold text-slate-900"}>
                    {invoice.invoiceNumber || "—"}
                  </div>
                  <div className={`mt-1 text-sm ${currentTheme.muted}`}>
                    {invoice.client?.name || invoice.clientName || "—"}
                  </div>
                </div>

                <span
                  className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${getInvoiceStatusBadgeClasses(
                    invoiceStatus,
                    theme
                  )}`}
                >
                  {getInvoiceStatusLabel(invoiceStatus, labels)}
                </span>
              </div>

             <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
  <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
    <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
      {labels.invoiceDate}
    </div>
    <div className="mt-1 break-words font-medium">
      {invoice.invoiceDate || "—"}
    </div>
  </div>

  <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700">
    <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
      {labels.dueDate}
    </div>
    <div className="mt-1 break-words font-medium">
      {invoice.dueDate || "—"}
    </div>
  </div>

  <div className="min-w-0 rounded-2xl border border-slate-200/70 p-3 dark:border-slate-700 sm:col-span-2">
    <div className={`text-[10px] font-bold uppercase tracking-[0.08em] ${currentTheme.muted}`}>
      {labels.grandTotalLabel}
    </div>
    <div className="mt-1 break-words font-semibold">
      {formatCurrency(
        invoice.grandTotal || invoice.total || invoice.subtotal,
        invoice.currency || company.currency || "EUR"
      )}
    </div>
  </div>
</div>

              <div className="mt-4 flex gap-2">
                <UIButton
                  type="button"
                  onClick={() => openInvoice(invoice)}
                  theme={theme}
                  variant="primary"
                  size="md"
                >
                  <FolderOpen className="h-4 w-4" />
                  {labels.open}
                </UIButton>

                <UIButton
                  type="button"
                  onClick={(e) => deleteInvoice(e, invoice.id)}
                  theme={theme}
                  variant="softDanger"
                  size="md"
                >
                  <Trash2 className="h-4 w-4" />
                  {labels.delete}
                </UIButton>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    <div className={`flex items-center justify-between gap-3 border-t px-4 py-4 sm:px-5 ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
      <UIButton
        type="button"
        onClick={handlePrevHistoryPage}
        disabled={historyPage <= 1 || historyLoading}
        theme={theme}
        variant="secondary"
        size="md"
      >
        {labels.previous}
      </UIButton>

      <div className={`text-sm font-semibold ${currentTheme.muted}`}>
        {labels.page} {historyPage}
      </div>

      <UIButton
        type="button"
        onClick={handleNextHistoryPage}
        disabled={!historyHasMore || historyLoading}
        theme={theme}
        variant="secondary"
        size="md"
      >
        {labels.next}
      </UIButton>
    </div>
  </div>
)}
          </div>

          <div className="no-print space-y-4">
            <div className={`rounded-[32px] border p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl ${currentTheme.surface}`}>
              <InvoicePdfToolbar
                labels={labels}
                invoice={{
                  invoiceNumber: activeInvoice?.invoiceNumber || invoiceNumber,
                  invoiceDate: activeInvoice?.invoiceDate || invoiceDate,
                  clientName: selectedClient?.name || clientName,
                  companyName: selectedClient?.companyName || "",
                }}
                client={selectedClient}
                onPrint={handlePrint}
              />

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <UIButton
                  type="button"
                  onClick={handleDownloadPdf}
                  theme={theme}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                >
                  {labels.downloadPdf}
                </UIButton>

                <UIButton
                  type="button"
                  onClick={handlePrint}
                  theme={theme}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {labels.print}
                </UIButton>
              </div>
            </div>

            <div className={`rounded-[32px] border p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl ${currentTheme.surface}`}>
              <InvoiceReminderToolbar
                invoice={reminderInvoice}
                client={selectedClient}
                company={{
                  name: company.companyName || "BauHeld",
                  phone: company.phone || "",
                }}
                lang={lang}
              />
            </div>
          </div>

          <div className={`rounded-[32px] border p-3 sm:p-4 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl ${currentTheme.surface}`}>
            <div className="mb-5 px-2 no-print">
              <h3 className={titleClass(theme)}>{labels.previewTitle}</h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {labels.previewSubtitle}
              </p>
            </div>

            <div ref={printRef} className="invoice-document-shell invoice-document-print-root">
              <InvoiceDocument
                invoice={activeInvoice}
                labels={labels}
                formatCurrency={formatCurrency}
                companyOverride={company}
                clientOverride={selectedClient}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
      <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-300">
        {label}
      </div>
      <div className="mt-2 text-lg font-black">{value}</div>
    </div>
  );
}

function isMeaningfulInvoiceValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

function mergeInvoiceData(...sources) {
  return sources.reduce((acc, source) => {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (isMeaningfulInvoiceValue(value)) acc[key] = value;
    });
    return acc;
  }, {});
}

function buildInvoiceSenderLines(company = {}) {
  const cityLine = [company.postalCode, company.city].filter(Boolean).join(" ");
  const primary = [
    company.ownerName || company.companyName,
    company.address,
    cityLine,
    company.country,
  ].filter((line) => String(line || "").trim());

  if (primary.length > 1) return primary;

  return [
    company.ownerName || company.companyName,
    company.email,
    company.phone,
    company.website,
  ].filter((line) => String(line || "").trim());
}

function buildInvoiceSenderInline(company = {}) {
  const cityLine = [company.postalCode, company.city].filter(Boolean).join(" ");
  const parts = [
    company.ownerName || company.companyName,
    company.address,
    cityLine,
    company.country,
  ].filter((line) => String(line || "").trim());

  if (parts.length >= 2) return parts.join(" - ").toUpperCase();

  return buildInvoiceSenderLines(company)
    .slice(0, 4)
    .join(" - ")
    .toUpperCase();
}

function InvoiceDocument({ invoice, labels, formatCurrency, companyOverride = {}, clientOverride = {} }) {
  const items =
    Array.isArray(invoice?.items) && invoice.items.length
      ? invoice.items
      : [{ ...EMPTY_ITEM }];

  const subtotal = Number(invoice?.subtotal) || 0;
  const vatRate = Number(invoice?.vatRate) || 0;
  const vatAmount = Number(invoice?.vatAmount) || 0;
  const total = Number(invoice?.total) || subtotal;

  const company = mergeInvoiceData(invoice?.company || {}, companyOverride || {});

  const client = mergeInvoiceData(invoice?.client || {}, clientOverride || {});

  const companyName = company.companyName || company.ownerName || "Bauheld";
  const senderLines = buildInvoiceSenderLines(company);
  const senderInline = buildInvoiceSenderInline(company);

  const recipientLines = String(client.address || "")
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4);

  if (!recipientLines.length) {
    [client.email, client.phone]
      .filter(Boolean)
      .forEach((line) => recipientLines.push(line));
  }

  const withMetaColon = (value, fallback) => {
    const base = String(value || fallback || "").trim().replace(/\s*:\s*$/, "");
    return `${base}:`;
  };
  const documentTitle = String(labels.title || "Rechnung").toUpperCase();
  const metaInvoiceNumberLabel = withMetaColon(documentTitle === "RECHNUNG" ? "Rechnungsnr." : labels.invoiceNumber, "Rechnungsnummer");
  const metaInvoiceDateLabel = withMetaColon(labels.invoiceDate, "Rechnungsdatum");
  const metaDueDateLabel = withMetaColon(labels.dueDate, "Fälligkeitsdatum");
  const companyInfoLines = [company.email, company.phone, company.website].filter(Boolean);
  const fallbackCompanyInfoLines = [
    company.address,
    [company.postalCode, company.city].filter(Boolean).join(" "),
    company.country,
  ].filter(Boolean);

  const brandLines = (companyInfoLines.length ? companyInfoLines : fallbackCompanyInfoLines).slice(0, 3);
  const recipientName = client.name || client.companyName || "—";

  return (
    <div className="invoice-document">
      <div className="invoice-header-layout">
        <div className="invoice-header-left">
          <div className="invoice-document-title">{documentTitle}</div>
          <div className="invoice-sender-block">
            <div className="invoice-sender-inline-row">{senderInline || companyName.toUpperCase()}</div>
          </div>
          <div className="invoice-recipient-block">
            <div className="invoice-section-label">{labels.recipientSection}</div>
            <div className="invoice-recipient-name">{recipientName}</div>
            {recipientLines.length ? (
              recipientLines.map((line, index) => (
                <div key={index} className="invoice-recipient-line">
                  {line}
                </div>
              ))
            ) : (
              <div className="invoice-recipient-line">—</div>
            )}
          </div>
        </div>

        <div className="invoice-header-right">
          <div className="invoice-brand-card">
            <div className="invoice-brand-row">
              <div className="invoice-logo-box">
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={companyName}
                    className="invoice-logo-image"
                  />
                ) : (
                  <span className="invoice-logo-fallback">
                    {String(companyName).slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="invoice-brand-copy">
                <div className="invoice-brand-name">{companyName}</div>
                {brandLines.map((line, index) => (
                  <div key={index} className="invoice-brand-line">{line}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="invoice-meta-panel">
            <MetaRow
              label={metaInvoiceNumberLabel}
              value={invoice?.invoiceNumber || "—"}
            />
            <MetaRow
              label={metaInvoiceDateLabel}
              value={invoice?.invoiceDate || "—"}
            />
            <MetaRow
              label={metaDueDateLabel}
              value={invoice?.dueDate || "—"}
            />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <table className="invoice-table">
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>{labels.description}</th>
              <th style={{ width: 90, textAlign: "center" }}>{labels.quantity}</th>
              <th style={{ width: 150, textAlign: "right" }}>{labels.unitPrice}</th>
              <th style={{ width: 130, textAlign: "right" }}>{labels.lineTotal}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const lineTotal =
                (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);

              return (
                <tr key={index}>
                  <td>{item.description || "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    {Number(item.quantity) || 0}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatCurrency(item.unitPrice, invoice?.currency || "EUR")}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 800 }}>
                    {formatCurrency(lineTotal, invoice?.currency || "EUR")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="doc-grid-2" style={{ alignItems: "start" }}>
        <div className="invoice-card">
          <div className="doc-label" style={{ marginBottom: 12 }}>
            {labels.notes}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {invoice?.notes || labels.thankYou}
          </div>
        </div>

        <div className="doc-total-card">
          <Row
            label={labels.subtotalLabel}
            value={formatCurrency(subtotal, invoice?.currency || "EUR")}
            compact
          />
          {vatRate >= 0 && (
            <div style={{ marginTop: 8 }}>
              <Row
                label={`${labels.vatLabel} (${vatRate}%)`}
                value={formatCurrency(vatAmount, invoice?.currency || "EUR")}
                compact
              />
            </div>
          )}
          <div style={{ height: 1, background: "#dbe5f2", margin: "12px 0" }} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 12,
              alignItems: "end",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                paddingRight: 10,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {labels.grandTotalLabel}
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              {formatCurrency(total, invoice?.currency || "EUR")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function MetaRow({ label, value }) {
  return (
    <div className="invoice-meta-row">
      <div className="invoice-meta-label">{label}</div>
      <div className="invoice-meta-value">{value}</div>
    </div>
  );
}

function Row({ label, value, strong = false, compact = false }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 12,
        alignItems: compact ? "center" : "start",
      }}
    >
      <div
        className="doc-muted"
        style={{
          fontSize: 14,
          paddingRight: 10,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 14,
          fontWeight: strong ? 800 : 700,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}


function Field({ label, value, onChange, type = "text" }) {
  const { theme } = usePanelTheme();

  return (
    <div>
      <label className={fieldLabelClass(theme)}>{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass(theme)}
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange }) {
  const { theme } = usePanelTheme();

  return (
    <div>
      <label className={fieldLabelClass(theme)}>{label}</label>
      <textarea
        rows={4}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={textareaClass(theme)}
      />
    </div>
  );
}

function titleClass(theme) {
  return theme === "dark"
    ? "text-xl font-bold tracking-tight text-slate-100"
    : "text-xl font-bold tracking-tight text-slate-900";
}

function fieldLabelClass(theme) {
  return theme === "dark"
    ? "mb-2 block text-sm font-semibold text-slate-300"
    : "mb-2 block text-sm font-semibold text-slate-700";
}

function inputClass(theme) {
  return `h-12 w-full rounded-[20px] border px-4 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-gradient-to-b from-white to-slate-50 text-slate-900"
  }`;
}

function textareaClass(theme) {
  return `w-full rounded-[20px] border px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-gradient-to-b from-white to-slate-50 text-slate-900"
  }`;
}

function softPanelClass(theme) {
  return theme === "dark"
    ? "rounded-3xl border border-slate-700 bg-slate-900/60 p-5"
    : "rounded-3xl border border-slate-200 bg-slate-50 p-5";
}

